import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import { supabase } from '../lib/supabase';
import { getCorrectRedirectURL, logRedirectInfo } from '../utils/domainUtils';
import { checkUserPromoCode, checkUserIsAdmin } from '../utils/promoCodeUtils';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [hasPromoCode, setHasPromoCode] = useState(null); // null = 아직 확인 안됨
  const [isAdmin, setIsAdmin] = useState(false);
  
  // user state를 추적하기 위한 ref
  const userRef = useRef(null);
  
  // user state가 변경될 때마다 ref 업데이트
  useEffect(() => {
    userRef.current = user;
  }, [user]);



  useEffect(() => {
    // 초기 세션 확인
    const getInitialSession = async () => {
      
      try {
        const { data: { session }, error } = await supabase.auth.getSession();
        
        if (error) {
          console.error('❌ Error getting session:', error);
        } else if (session?.user) {
          setUser(session.user);
          
          // 프로필 생성은 일단 스킵하고 기본 프로필 설정
          const basicProfile = {
            id: session.user.id,
            email: session.user.email,
            name: session.user.user_metadata?.full_name || session.user.user_metadata?.name || '사용자',
            avatar_url: session.user.user_metadata?.avatar_url || session.user.user_metadata?.picture || null,
          };
          
          setProfile(basicProfile);
          
          // 프로모션 코드 확인 (비동기로 진행, 차단하지 않음)
          checkUserPromoCode(session.user.id).then(result => {
            setHasPromoCode(result.hasPromoCode);
          });
          
          // 관리자 확인 (비동기로 진행)
          checkUserIsAdmin(session.user.id).then(result => {
            setIsAdmin(result.isAdmin);
          });
        } else {
          setHasPromoCode(null);
          setIsAdmin(false);
        }
      } catch (error) {
        console.error('💥 세션 확인 중 오류:', error);
      }
      
      //console.log('🏁 초기 로딩 완료 - loading을 false로 설정');
      setLoading(false);
    };

    getInitialSession();

    // Auth 상태 변화 리스너
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        // 토큰 갱신 이벤트는 무시 (탭 전환 시 발생)
        if (event === 'TOKEN_REFRESHED') {
          return;
        }
        
        //console.log('🔄 Auth state changed:', event, session?.user?.id);
        
        if (session?.user) {
          // 이미 같은 사용자가 로그인되어 있으면 업데이트하지 않음
          if (userRef.current && userRef.current.id === session.user.id) {
            return;
          }
          
          setUser(session.user);
          
          // 기본 프로필 설정 (DB 저장 없이)
          const basicProfile = {
            id: session.user.id,
            email: session.user.email,
            name: session.user.user_metadata?.full_name || session.user.user_metadata?.name || '사용자',
            avatar_url: session.user.user_metadata?.avatar_url || session.user.user_metadata?.picture || null,
          };
          
          setProfile(basicProfile);
          
          // 프로모션 코드 확인 (비동기로 진행, 차단하지 않음)
          checkUserPromoCode(session.user.id).then(result => {
            setHasPromoCode(result.hasPromoCode);
          });
          
          // 관리자 확인 (빔동기로 진행)
          checkUserIsAdmin(session.user.id).then(result => {
            setIsAdmin(result.isAdmin);
          });
        } else {
          setUser(null);
          setProfile(null);
          setHasPromoCode(null);
          setIsAdmin(false);
        }
        //console.log('🏁 Auth 상태 변화 처리 완료');
      }
    );

    return () => subscription.unsubscribe();
  }, []); // 빈 의존성 배열 - user를 제거

  // Google 로그인
  const signInWithGoogle = async () => {
    try {
      // 도메인 유틸리티를 사용한 정확한 리다이렉트 URL 결정
      const { domainInfo, redirectURL } = logRedirectInfo();
      
      const { data, error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: redirectURL,
          queryParams: {
            access_type: 'offline',
            prompt: 'consent',
          }
        }
      });

      if (error) {
        console.error('Error signing in with Google:', error);
        return { success: false, error: error.message };
      }

      return { success: true };
    } catch (error) {
      console.error('Error in signInWithGoogle:', error);
      return { success: false, error: error.message };
    }
  };

  // 이메일 로그인
  const signInWithEmail = async (email, password) => {
    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password
      });

      if (error) {
        console.error('Error signing in with email:', error);
        return { success: false, error: error.message };
      }

      return { success: true };
    } catch (error) {
      console.error('Error in signInWithEmail:', error);
      return { success: false, error: error.message };
    }
  };

  // 이메일 회원가입
  const signUpWithEmail = async (email, password, name) => {
    try {
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            full_name: name,
          }
        }
      });

      if (error) {
        console.error('Error signing up with email:', error);
        return { success: false, error: error.message };
      }

      return { success: true, data };
    } catch (error) {
      console.error('Error in signUpWithEmail:', error);
      return { success: false, error: error.message };
    }
  };

  // 로그아웃
  const signOut = async () => {
    try {
      // 먼저 현재 세션 상태를 확인
      const { data: { session } } = await supabase.auth.getSession();
      
      // 세션이 있는 경우에만 서버에 로그아웃 요청
      if (session) {
        const { error } = await supabase.auth.signOut();
        
        if (error) {
          console.error('Error signing out:', error);
          // 서버 로그아웃이 실패해도 클라이언트 상태는 정리
        }
      }

      // 클라이언트 상태 정리 (항상 실행)
      setUser(null);
      setProfile(null);
      
      // 로컬 스토리지 정리 (선택적)
      try {
        localStorage.removeItem('supabase.auth.token');
      } catch (e) {
        // 로컬 스토리지 접근 실패 시 무시
      }
      
      console.log('✅ 로그아웃 완료');
      return { success: true };
    } catch (error) {
      console.error('Error in signOut:', error);
      
      // 에러가 발생해도 클라이언트 상태는 정리
      setUser(null);
      setProfile(null);
      
      return { success: false, error: error.message };
    }
  };

  // 프로필 업데이트 (메모리에서만)
  const updateProfile = async (updates) => {
    try {
      if (!user) {
        return { success: false, error: 'No user logged in' };
      }

      // 일단 메모리에서만 업데이트
      const updatedProfile = { ...profile, ...updates };
      setProfile(updatedProfile);
      
      return { success: true, data: updatedProfile };
    } catch (error) {
      console.error('Error in updateProfile:', error);
      return { success: false, error: error.message };
    }
  };

  // 프로모션 코드 상태를 다시 확인하는 함수
  const refreshPromoCodeStatus = async () => {
    if (!user?.id) return;
    
    const result = await checkUserPromoCode(user.id);
    setHasPromoCode(result.hasPromoCode);
  };

  const value = {
    user,
    profile,
    loading,
    hasPromoCode,
    isAdmin,
    signInWithGoogle,
    signInWithEmail,
    signUpWithEmail,
    signOut,
    updateProfile,
    refreshPromoCodeStatus, // 새로 추가
    // 기존 호환성을 위한 별칭들
    login: signInWithGoogle,
    logout: signOut,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};